/**
 * État d'installation de la PWA côté React.
 * Expose la disponibilité du dialogue natif (Android/desktop), le cas iOS
 * (installation manuelle via Safari), et l'état "déjà installée".
 */

import { useSyncExternalStore, useCallback } from 'react';
import {
    subscribePwaInstall,
    canPromptInstall,
    wasInstalled,
    promptInstall,
} from '@/lib/pwaInstall';

function isStandalone(): boolean {
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    );
}

function isIOS(): boolean {
    const ua = window.navigator.userAgent;
    // iPadOS 13+ se présente comme un Mac : on le rattrape via le support tactile.
    return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
}

/** Safari iOS uniquement : les autres navigateurs iOS (Chrome, Firefox...) ne savent pas installer. */
function isIOSSafari(): boolean {
    if (!isIOS()) return false;
    return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(window.navigator.userAgent);
}

interface PwaInstallState {
    /** Android/desktop : le dialogue natif est prêt à être déclenché. */
    canInstall: boolean;
    /** iOS Safari non installé : montrer les instructions manuelles. */
    iosInstallable: boolean;
    /** L'app tourne déjà en mode installé (ou vient de l'être). */
    isInstalled: boolean;
    install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

export function usePwaInstall(): PwaInstallState {
    const canInstall = useSyncExternalStore(subscribePwaInstall, canPromptInstall, () => false);
    const installedEvent = useSyncExternalStore(subscribePwaInstall, wasInstalled, () => false);

    const standalone = isStandalone();
    const install = useCallback(() => promptInstall(), []);

    return {
        canInstall,
        iosInstallable: isIOSSafari() && !standalone,
        isInstalled: installedEvent || standalone,
        install,
    };
}

export default usePwaInstall;
